import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../../constants/theme';
import { loginUser, loginWithOAuth, clearError, selectAuth, selectIsAuthenticated } from '../../store/authSlice';
import { AppDispatch } from '../../store';
import { OAuthProvider } from '../../services/AuthService';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    ${Colors.primary}10 0%, 
    ${Colors.secondary}05 100%
  );
  padding: ${Spacing.lg}px;
`;

const LoginCard = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.xl}px;
  box-shadow: ${Shadows.xl};
  padding: ${Spacing.xxxl}px;
  width: 100%;
  max-width: 480px;
  border: 1px solid ${Colors.borderLight};
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: ${Spacing.xxxl}px;
`;

const LogoImage = styled.img`
  width: 100%;
  max-width: 320px;
  height: auto;
  margin: 0 auto ${Spacing.lg}px;
  display: block;
`;

const Subtitle = styled.p`
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.lg}px;
  margin: 0;
`;

const Form = styled.form`
  margin-bottom: ${Spacing.xl}px;
`;

const FormGroup = styled.div`
  margin-bottom: ${Spacing.lg}px;
`;

const Label = styled.label`
  display: block;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const Input = styled.input`
  width: 100%;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  border: 2px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.md}px;
  transition: all 0.2s ease;
  background: ${Colors.background};

  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 3px ${Colors.primary}20;
  }

  &:invalid {
    border-color: ${Colors.danger};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  margin-bottom: ${Spacing.lg}px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${Colors.primary};
`;

const CheckboxLabel = styled.label`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  cursor: pointer;
`;

const LoginButton = styled.button<{ loading?: boolean }>`
  width: 100%;
  padding: ${Spacing.lg}px;
  background: ${props => props.loading ? Colors.textTertiary : Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  cursor: ${props => props.loading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.sm}px;

  &:hover:not(:disabled) {
    background: ${Colors.primaryDark};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${Spacing.xl}px 0;
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.sm}px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${Colors.border};
  }

  &::before {
    margin-right: ${Spacing.lg}px;
  }

  &::after {
    margin-left: ${Spacing.lg}px;
  }
`;

const OAuthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

const OAuthButton = styled.button<{ provider: string }>`
  width: 100%;
  padding: ${Spacing.lg}px;
  background: ${Colors.surface};
  border: 2px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.md}px;
  color: ${Colors.textPrimary};

  &:hover {
    border-color: ${Colors.primary};
    background: ${Colors.backgroundDark};
  }

  ${props => props.provider === 'microsoft' && `
    border-color: #0078d4;
    color: #0078d4;
    &:hover {
      background: #0078d420;
      border-color: #0078d4;
    }
  `}
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, ${Colors.danger}05 0%, ${Colors.surface} 100%);
  border: 1px solid ${Colors.danger}20;
  color: ${Colors.textSecondary};
  padding: ${Spacing.md}px ${Spacing.xl}px ${Spacing.md}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.md}px;
  margin-bottom: ${Spacing.lg}px;
  font-size: ${FontSizes.sm}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  position: relative;
  opacity: 0.95;
  animation: gentleFadeIn 0.4s ease-out;
  
  @keyframes gentleFadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 0.95;
      transform: translateY(0);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${Colors.danger};
    opacity: 0.5;
    border-radius: ${BorderRadius.md}px 0 0 ${BorderRadius.md}px;
  }
  
  & > span:first-child {
    color: ${Colors.danger};
    opacity: 0.7;
    font-size: ${FontSizes.md}px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${Spacing.md}px;
  right: ${Spacing.md}px;
  background: none;
  border: none;
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.lg}px;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
  transition: all 0.2s ease;
  line-height: 1;
  
  &:hover {
    opacity: 0.8;
    color: ${Colors.danger};
    transform: scale(1.1);
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const DemoCredentials = styled.div`
  background: linear-gradient(135deg, ${Colors.primary}03 0%, ${Colors.surface} 100%);
  border: 1px solid ${Colors.borderLight};
  padding: ${Spacing.lg}px;
  border-radius: ${BorderRadius.md}px;
  margin-top: ${Spacing.xl}px;
  font-size: ${FontSizes.sm}px;
  position: relative;
  opacity: 0.85;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 1;
    border-color: ${Colors.primary}30;
    background: linear-gradient(135deg, ${Colors.primary}05 0%, ${Colors.surface} 100%);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, ${Colors.primary}, ${Colors.secondary});
    opacity: 0.4;
    border-radius: ${BorderRadius.md}px 0 0 ${BorderRadius.md}px;
  }
`;

const DemoTitle = styled.div`
  font-weight: ${FontWeights.medium};
  color: ${Colors.textSecondary};
  margin-bottom: ${Spacing.sm}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  font-size: ${FontSizes.xs}px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.7;
`;

const DemoCredential = styled.div`
  margin-bottom: ${Spacing.xs}px;
  font-family: monospace;
  color: ${Colors.textSecondary};
`;

interface LoginScreenProps {
  onLoginSuccess?: () => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLoginSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useSelector(selectAuth);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false); // Only show errors after first submit
  const [showError, setShowError] = useState(true); // Control error visibility

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation errors on input (but only if they've submitted before)
    if (hasSubmitted && validationErrors.length > 0) {
      setValidationErrors([]);
      setShowError(true); // Reset error visibility when clearing
      // Clear error from Redux too if it exists
      if (error) {
        dispatch(clearError());
      }
    }
  };

  const handleCloseError = () => {
    setShowError(false);
    setValidationErrors([]);
    if (error) {
      dispatch(clearError());
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    // Only show the most important error to reduce noise
    if (!formData.email) {
      errors.push(t('validation.emailRequired', 'Please enter your email'));
      setValidationErrors(errors);
      return false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.push(t('validation.emailInvalid', 'Invalid email format'));
      setValidationErrors(errors);
      return false;
    }

    if (!formData.password) {
      errors.push(t('validation.passwordRequired', 'Please enter your password'));
      setValidationErrors(errors);
      return false;
    } else if (formData.password.length < 6) {
      errors.push(t('validation.passwordLength', 'Password too short'));
      setValidationErrors(errors);
      return false;
    }

    setValidationErrors([]);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    setShowError(true); // Show errors on submit

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(loginUser(formData)).unwrap();
      onLoginSuccess?.();
      // Navigation will happen through the useEffect hook when isAuthenticated changes
    } catch (error) {
      // Error is handled by Redux
      console.error('Login failed:', error);
    }
  };

  const handleOAuthLogin = async (providerType: 'microsoft' | 'google') => {
    const provider: OAuthProvider = {
      type: providerType,
      clientId: providerType === 'microsoft' ? 'your-ms-client-id' : 'your-google-client-id',
      redirectUri: `${window.location.origin}/auth/callback`,
    };

    try {
      await dispatch(loginWithOAuth(provider)).unwrap();
      onLoginSuccess?.();
    } catch (error) {
      console.error('OAuth login failed:', error);
    }
  };

  const fillDemoCredentials = (role: 'hr' | 'manager' | 'recruiter') => {
    const credentials = {
      hr: { email: 'hr.leader@cloudonmars.com', password: 'demo123' },
      manager: { email: 'manager@cloudonmars.com', password: 'demo123' },
      recruiter: { email: 'recruiter@cloudonmars.com', password: 'demo123' },
    };

    setFormData(prev => ({
      ...prev,
      ...credentials[role]
    }));
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoSection>
          <LogoImage 
            src="/assets/Clouds On Mars - Logotype with Slogan - Martian Orange - Horizontal.png" 
            alt="Clouds on Mars - Let Your Data Make an Impact"
          />
          <Subtitle>{t('auth.subtitle', 'HR Analytics Platform')}</Subtitle>
        </LogoSection>

        {hasSubmitted && showError && (error || validationErrors.length > 0) && (
          <ErrorMessage>
            <span>•</span>
            <div style={{ flex: 1 }}>
              {error && <div>{error}</div>}
              {validationErrors.map((err, index) => (
                <div key={index}>{err}</div>
              ))}
            </div>
            <CloseButton 
              type="button"
              onClick={handleCloseError}
              aria-label="Close error message"
            >
              ×
            </CloseButton>
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">{t('auth.email', 'Email Address')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('auth.emailPlaceholder', 'Enter your email')}
              required
              autoComplete="email"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">{t('auth.password', 'Password')}</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={t('auth.passwordPlaceholder', 'Enter your password')}
              required
              autoComplete="current-password"
            />
          </FormGroup>

          <CheckboxGroup>
            <Checkbox
              id="rememberMe"
              name="rememberMe"
              type="checkbox"
              checked={formData.rememberMe}
              onChange={handleInputChange}
            />
            <CheckboxLabel htmlFor="rememberMe">
              {t('auth.rememberMe', 'Keep me signed in')}
            </CheckboxLabel>
          </CheckboxGroup>

          <LoginButton type="submit" disabled={isLoading} loading={isLoading}>
            {isLoading ? (
              <>
                <LoadingSpinner />
                {t('auth.signingIn', 'Signing in...')}
              </>
            ) : (
              t('auth.signIn', 'Sign In')
            )}
          </LoginButton>
        </Form>

        <Divider>{t('auth.orContinueWith', 'or continue with')}</Divider>

        <OAuthSection>
          <OAuthButton
            type="button"
            provider="microsoft"
            onClick={() => handleOAuthLogin('microsoft')}
            disabled={isLoading}
          >
            <span>🏢</span>
            {t('auth.signInWithMicrosoft', 'Sign in with Microsoft')}
          </OAuthButton>

          <OAuthButton
            type="button"
            provider="google"
            onClick={() => handleOAuthLogin('google')}
            disabled={isLoading}
          >
            <span>🔍</span>
            {t('auth.signInWithGoogle', 'Sign in with Google')}
          </OAuthButton>
        </OAuthSection>

        <DemoCredentials>
          <DemoTitle>{t('auth.demoCredentials', 'Demo Credentials (Development Only)')}</DemoTitle>
          <DemoCredential>
            <strong>{t('auth.hrLeader', 'HR Leader')}:</strong>{' '}
            <button 
              type="button" 
              onClick={() => fillDemoCredentials('hr')}
              style={{ background: 'none', border: 'none', color: Colors.primary, cursor: 'pointer', textDecoration: 'underline' }}
            >
              hr.leader@cloudonmars.com
            </button>
          </DemoCredential>
          <DemoCredential>
            <strong>{t('auth.manager', 'Manager')}:</strong>{' '}
            <button 
              type="button" 
              onClick={() => fillDemoCredentials('manager')}
              style={{ background: 'none', border: 'none', color: Colors.primary, cursor: 'pointer', textDecoration: 'underline' }}
            >
              manager@cloudonmars.com
            </button>
          </DemoCredential>
          <DemoCredential>
            <strong>{t('auth.recruiter', 'Recruiter')}:</strong>{' '}
            <button 
              type="button" 
              onClick={() => fillDemoCredentials('recruiter')}
              style={{ background: 'none', border: 'none', color: Colors.primary, cursor: 'pointer', textDecoration: 'underline' }}
            >
              recruiter@cloudonmars.com
            </button>
          </DemoCredential>
          <DemoCredential style={{ marginTop: Spacing.sm, fontStyle: 'italic' }}>
            {t('auth.passwordForAll', 'Password for all')}: <strong>demo123</strong>
          </DemoCredential>
        </DemoCredentials>
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginScreen;